/* START_MODULE_CONTRACT
 * PURPOSE: Frontend UI module: EditUserDialog.
 * SCOPE: React component or page.
 * DEPENDS: M-FE-API, M-FE-AUTH
 * LINKS: M-FE-ADMIN
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * EditUserDialog - Frontend UI module: EditUserDialog.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-02 - GRACE coverage: контракты функций, парные END_METHOD, блоки/логи для логики.
 * END_CHANGE_SUMMARY
 */

import { useState, useEffect, useRef } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';
import { useDialogFormReady } from '../../../hooks/useDialogFormReady';
import { useBlurValidatedField } from '../../../hooks/useBlurValidatedField';
import { isEmptyOrMinLength, isNonEmptyTrimmed } from '../../../utils/formValidators';

/* START_METHOD_EditUserDialog */
/* START_CONTRACT: EditUserDialog
 * PURPOSE: Диалог редактирования пользователя (email и опциональный пароль).
 * INPUTS: { open: boolean, onClose: ()=>void, onUpdate: (id,payload)=>Promise, user: object }.
 * OUTPUTS: { JSX.Element|null - диалог }.
 * SIDE_EFFECTS: вызов onUpdate (HTTP наверх); локальный state.
 * LINKS: M-FE-ADMIN
 * END_CONTRACT: EditUserDialog
 */
export default function EditUserDialog({ open, onClose, onUpdate, user }) {
  const firstFieldRef = useRef(null);
  const { formReady, resetFormReady, dialogTransitionSlotProps } = useDialogFormReady({ firstFieldRef });
  const emailField = useBlurValidatedField('', isNonEmptyTrimmed, formReady);
  const passwordField = useBlurValidatedField('', isEmptyOrMinLength(6), formReady);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) {
      emailField.reset(user.email);
      passwordField.reset('');
      setError('');
    }
  }, [user]);

  if (!user) return null;

  const canSave = emailField.isValid && passwordField.isValid && !loading;

  const handleClose = () => {
    if (loading) return;
    resetFormReady();
    onClose();
  };

  /* START_METHOD_handleUpdate */
  /* START_CONTRACT: handleUpdate
   * PURPOSE: Собрать payload и обновить пользователя; при успехе закрыть, иначе показать ошибку.
   * INPUTS: {}.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: onUpdate (HTTP); setLoading/setError; onClose.
   * LINKS: M-FE-ADMIN
   * END_CONTRACT: handleUpdate
   */
  const handleUpdate = async (event) => {
    if (event) event.preventDefault();
    if (!canSave) {
      /* START_BLOCK_SUBMIT_BLOCKED */
      emailField.touch();
      passwordField.touch();
      return;
      /* END_BLOCK_SUBMIT_BLOCKED */
    }
    setLoading(true);
    setError('');
    try {
      const payload = { email: emailField.value.trim() };
      if (passwordField.value) {
        payload.password = passwordField.value;
      }
      await onUpdate(user.id, payload);
      /* START_BLOCK_SUBMIT_OK */
      onClose();
      /* END_BLOCK_SUBMIT_OK */
    } catch (err) {
      /* START_BLOCK_SUBMIT_FAIL */
      console.warn('[EditUserDialog][handleUpdate][SUBMIT_FAIL] update user failed', {
        status: err.response?.status,
      });
      setError(err.response?.data?.message || 'Ошибка обновления пользователя');
      /* END_BLOCK_SUBMIT_FAIL */
    } finally {
      setLoading(false);
    }
  };
  /* END_METHOD_handleUpdate */

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{ transition: dialogTransitionSlotProps }}
    >
      <form onSubmit={handleUpdate} noValidate>
        <DialogTitle sx={{ fontSize: '1rem' }}>Редактирование пользователя</DialogTitle>
        <DialogContent>
          <Collapse in={Boolean(error)} unmountOnExit>
            <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>
              {error}
            </Alert>
          </Collapse>
          <TextField
            inputRef={firstFieldRef}
            required
            fullWidth
            label="Email"
            type="email"
            value={emailField.value}
            onChange={emailField.onChange}
            onBlur={emailField.onBlur}
            margin="normal"
            size="small"
            disabled={loading}
            error={emailField.showError}
            helperText={emailField.showError ? 'Введите email' : ' '}
          />
          <TextField
            fullWidth
            label="Пароль"
            type="password"
            value={passwordField.value}
            onChange={passwordField.onChange}
            onBlur={passwordField.onBlur}
            margin="normal"
            size="small"
            disabled={loading}
            error={passwordField.showError}
            helperText={passwordField.showError ? 'Минимум 6 символов' : 'Оставьте пустым, чтобы не менять'}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button size="small" onClick={handleClose} disabled={loading}>
            Отмена
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="success"
            size="small"
            loading={loading}
            disabled={!canSave}
          >
            Сохранить
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
/* END_METHOD_EditUserDialog */
